// auth.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthService } from '@auth0/auth0-angular';
import { login, logout, setError, setUser, setStatistics, updateToken } from '../actions/auth.actions';
import { of, EMPTY } from 'rxjs';
import { map, catchError, tap, switchMap, take } from 'rxjs/operators';
import { GlobalService } from 'src/app/core/services/APIs/dashboard/global.service';
import { RoleService } from 'src/app/core/services/APIs/user/role.service';
import { loadRoles } from '../actions/role.actions';
import { Store } from '@ngrx/store';
import { transformPayload } from 'src/app/core/utils/array-utils';
import { loadSites } from '../actions/site.actions';
import { loadConstants } from '../actions/constants.actions';
import { environment } from 'src/environments/environment';
import { CustomToastService } from 'src/app/shared/services/custom-toast.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthTokenService } from 'src/app/core/services/auth-token.service';
import { LanguageService } from 'src/app/core/services/language.service';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private auth: AuthService,
    private globalService: GlobalService,
    private customToast: CustomToastService,
    private translateService: TranslateService,
    private authTokenService: AuthTokenService,
    private languageService: LanguageService
  ) {}

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      take(1),
      switchMap(({ user, token }) => {
        // If we already have user and token, just store them
        if (user && token) {
          this.storeAuthData(user, token);
          return of(setUser({ user }));
        }

        // Check if auth is already in progress
        if (sessionStorage.getItem('auth_in_progress')) {
          return EMPTY;
        }

        // Set auth in progress flag
        sessionStorage.setItem('auth_in_progress', 'true');

        // Otherwise initiate login flow
        return this.auth.loginWithRedirect({
          appState: { 
            returnTo: window.location.pathname 
          },
          authorizationParams: {
            organization: localStorage.getItem('org_id') || '',
            prompt: 'select_account',
            display: 'popup',
            response_type: 'token id_token',
          }
        }).pipe(
          switchMap(() =>
            this.auth.user$.pipe(
              take(1),
              switchMap((user) => {
                // Only get token if we don't have one in localStorage
                const authData = JSON.parse(localStorage.getItem('auth') || '{}');
                if (authData.token) {
                  return of({ user, token: authData.token });
                }
                // Let Auth0 SDK handle token management and refresh
                return this.auth.getAccessTokenSilently().pipe(
                  take(1),
                  map(token => ({
                    user,
                    token
                  }))
                );
              })
            )
          ),
          map(({ user, token }) => {
            sessionStorage.removeItem('auth_in_progress');
            this.storeAuthData(user, token);
            return login({ user, token });
          }),
          catchError((error) => {
            sessionStorage.removeItem('auth_in_progress');
            return of(setError({ error: error.message }));
          })
        );
      })
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        take(1),
        tap(() => {
          // Clear auth-related data
          localStorage.removeItem('auth');
          localStorage.removeItem('org_id');
          localStorage.removeItem('token_expires_at');
          localStorage.removeItem('roles');
          
          // Clear site-related data
          localStorage.removeItem('sites');
          localStorage.removeItem('site');
          localStorage.removeItem('types');
          localStorage.removeItem('type');
          localStorage.removeItem('statistics');
          
          // Clear session storage
          sessionStorage.removeItem('auth_in_progress');
          sessionStorage.removeItem('handling_auth');

          this.auth.logout({
            logoutParams: { 
              returnTo: window.location.origin,
              clientId: environment.auth.clientId
            }
          });
        })
      ),
    { dispatch: false }
  );

  // Handle token updates
  updateToken$ = createEffect(
    () => 
      this.actions$.pipe(
        ofType(updateToken),
        tap(({ token }) => {
          // Update token in the existing auth object in localStorage
          const authData = JSON.parse(localStorage.getItem('auth') || '{}');
          if (authData && authData.user) {
            authData.token = token;
            localStorage.setItem('auth', JSON.stringify(authData));
            
            // Update token expiry
            try {
              const tokenParts = token.split('.');
              const tokenPayload = JSON.parse(atob(tokenParts[1]));
              if (tokenPayload.exp) {
                localStorage.setItem('token_expires_at', (tokenPayload.exp * 1000).toString());
              }
            } catch (e) {
              console.error('Error parsing token:', e);
            }
          }
        })
      ),
    { dispatch: false }
  );

  persistAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(login),
        tap(async ({ user, token }) => {
          if (user?.org_id) {
            // Store org_id using the new method
            this.authTokenService.setOrgId(user.org_id);
            
            // First, make sure translations are loaded before proceeding with other actions
            // This blocks rendering until translations are ready
            await this.languageService.initialize();
            
            // Dispatch necessary actions
            this.store.dispatch(setUser({ user }));
            this.store.dispatch(loadRoles());
            this.store.dispatch(loadSites());
            this.store.dispatch(loadConstants());

            // Handle statistics
            this.handleStatistics();
          } else {
            // Clear auth progress flag before redirecting
            sessionStorage.removeItem('auth_in_progress');
            
            // If no org_id, trigger organization selection
            this.auth.loginWithRedirect({
              appState: { returnTo: window.location.pathname },
              authorizationParams: {
                prompt: 'select_account',
                display: 'popup',
                response_type: 'token id_token',
              }
            });
          }
        })
      ),
    { dispatch: false }
  );

  // This effect handles checking authentication status on app init
  checkAuth$ = createEffect(
    () =>
      this.auth.isAuthenticated$.pipe(
        take(1),
        switchMap((isAuthenticated) => {
          if (!isAuthenticated) {
            return this.auth.user$.pipe(
              take(1),
              switchMap(user => {
                if (user) {
                  // Use Auth0's SDK to handle token refresh
                  return this.auth.getAccessTokenSilently().pipe(
                    take(1),
                    tap(token => {
                      this.storeAuthData(user, token);
                      this.store.dispatch(setUser({ user }));
                    }),
                    catchError((error) => {
                      // this.customToast.showErrorModal(
                      //   error?.error?.error || this.translateService.instant('COMMON.ERRORS.AUTH_TOKEN_FAILED'),
                      //   this.translateService.instant('COMMON.ERROR')
                      // );
                      this.handleLogout();
                      return of(null);
                    })
                  );
                }
                return EMPTY;
              })
            );
          }
          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  private handleStatistics(): void {
    const storedStats = localStorage.getItem('statistics');
    if (!storedStats) {
      this.globalService.getStatistics().subscribe({
        next: (res: any) => {
          const transformedStats = transformPayload(res?.data);
          localStorage.setItem('statistics', JSON.stringify(transformedStats));
          this.store.dispatch(setStatistics({ statistics: transformedStats }));
        },
        error: (error) => {
          // this.customToast.showErrorModal(
          //   error?.error?.error || this.translateService.instant('COMMON.ERRORS.STATISTICS_LOADING_FAILED'),
          //   this.translateService.instant('COMMON.ERROR')
          // );
          console.log(error);
          throw error;
        }
      });
    } else {
      try {
        const statistics = JSON.parse(storedStats);
        this.store.dispatch(setStatistics({ statistics }));
      } catch (error: any) {
        // this.customToast.showErrorModal(
        //   error?.error?.error || this.translateService.instant('COMMON.ERRORS.STATISTICS_PARSE_FAILED'),
        //   this.translateService.instant('COMMON.ERROR')
        // );
        console.log(error);
        throw error;
      }
    }
  }

  // Helper method to store auth data consistently
  private storeAuthData(user: any, token: string): void {
    localStorage.setItem('org_id', user.org_id || '');
    localStorage.setItem('auth', JSON.stringify({ user, token }));
    
    // Store token expiry
    try {
      const tokenParts = token.split('.');
      const tokenPayload = JSON.parse(atob(tokenParts[1]));
      if (tokenPayload.exp) {
        localStorage.setItem('token_expires_at', (tokenPayload.exp * 1000).toString());
      }
    } catch (e) {
      console.error('Error parsing token:', e);
    }
  }

  private handleLogout(): void {
    // Clear auth-related data
    localStorage.removeItem('auth');
    localStorage.removeItem('org_id');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('roles');
    
    // Clear site-related data
    localStorage.removeItem('sites');
    localStorage.removeItem('site');
    localStorage.removeItem('types');
    localStorage.removeItem('type');
    localStorage.removeItem('statistics');
    
    // Clear session storage
    sessionStorage.removeItem('auth_in_progress');
    sessionStorage.removeItem('handling_auth');
    
    this.auth.logout({
      logoutParams: { 
        returnTo: window.location.origin,
        clientId: environment.auth.clientId
      }
    });
  }
}


